/**
 * The end-of-survey ad hoc checklist (spec §2.5, mockup screen 6).
 *
 * Four items, and only one of them carries a number: "clean pit of oil &
 * debris" is priced by the pit, so the surveyor supplies the count of DIRTY pits
 * and the office prices it. The other three flags are duration-based and the
 * office sets the duration from the job schedule. The rows used to say so in
 * a right-hand note each; D-033 R8/R11 — a badge says a state, and the
 * stepper already shows the count.
 *
 * A free-form add-item button, with its own per-item photo doorway, used to
 * sit below the checklist. The design authority named that photo picker
 * leftover from an old code base — inconsistent with the carousel every
 * other screen shows — and asked to remove individual items in favor of the
 * single free-form notes field every other screen already has (task 7,
 * controller ruling 7). `SectionScreen` renders that field, and the hand
 * (`PhotoShelf`), below this list — nothing about either is this component's
 * business. `AdHocItems.other` still exists on the document so an old record
 * still parses; this component never draws or writes it again.
 *
 * ── Writes ───────────────────────────────────────────────────────────────────
 * Every tap goes through `setStructure`, which is `store.updateDocument` — one
 * IndexedDB transaction, committed before the checkbox changes on screen. There
 * is no local draft state for the flags at all: what is drawn is what is on
 * disk.
 *
 * Rows are real `<button aria-pressed>` elements, 44px tall, with the label
 * inside the target — not a 20px checkbox with a caption beside it.
 */

import type { ReactNode } from 'react';

import {
  AD_HOC_FLAGS,
  CLEAN_PIT_LABEL,
  pitCount,
  setAdHocFlag,
  setCleanPits,
} from '../state/adHoc';
import { useSurvey } from '../state/SurveyProvider';
import { dirtyPitCountWrite } from '../state/surveyWrites';
import { Stepper } from './Stepper';

export function AdHocList(): ReactNode {
  const { doc, setStructure, readOnly } = useSurvey();
  const items = doc.adHoc;
  const pits = pitCount(doc);
  const dirty = items.cleanPitOfOilAndDebris;

  return (
    <div className="card card--flush">
      <ul className="adhoc__rows">
        {AD_HOC_FLAGS.map((flag) => (
          <li key={flag.key}>
            <button
              type="button"
              className={items[flag.key] ? 'adhoc adhoc--on' : 'adhoc'}
              aria-pressed={items[flag.key]}
              disabled={readOnly}
              onClick={() => {
                void setStructure((document) => {
                  setAdHocFlag(document, flag.key, !document.adHoc[flag.key]);
                });
              }}
            >
              <span className="adhoc__box" aria-hidden="true">
                ✓
              </span>
              <span className="adhoc__text">
                <span className="adhoc__name">{flag.label}</span>
              </span>
            </button>
          </li>
        ))}

        <li>
          <button
            type="button"
            className={dirty.flag ? 'adhoc adhoc--on' : 'adhoc'}
            aria-pressed={dirty.flag}
            disabled={readOnly}
            onClick={() => {
              void setStructure((document) => {
                const was = document.adHoc.cleanPitOfOilAndDebris.flag;
                // Turning it on starts at one dirty pit — the surveyor ticked
                // the row because they saw one. Turning it off zeroes the
                // count rather than remembering it (see setCleanPits).
                setCleanPits(document, !was, was ? 0 : Math.max(1, dirty.count));
              });
            }}
          >
            <span className="adhoc__box" aria-hidden="true">
              ✓
            </span>
            <span className="adhoc__text">
              <span className="adhoc__name">{CLEAN_PIT_LABEL}</span>
            </span>
          </button>

          {dirty.flag ? (
            <div className="adhoc__qtyrow">
              <Stepper
                label="How many pits are dirty?"
                value={dirty.count}
                min={1}
                disabled={readOnly}
                {...(pits > 0 ? { max: pits } : {})}
                onChange={(next) => {
                  // D-055: a count only — never re-ticks the row. A typed
                  // count committed as this stepper unmounts (iOS, no blur)
                  // after the row was unticked must not bring it back.
                  // D-056: a dropped count is NO_CHANGE — no push for it.
                  void setStructure(dirtyPitCountWrite(next));
                }}
              />
            </div>
          ) : null}
        </li>
      </ul>
    </div>
  );
}
